import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Checkbox, Divider, Form, Input, Popconfirm, Result, Select, Spin } from "antd";
import { useCallback, useRef, useState } from "react";
import { ESupportTicketQuestionType, routeLocationSupportTicketPost } from "@devowl-wp/api-real-commerce";
import { SupportFormConcernField } from "./concernField.js";
import { SupportFormErrorLocationAndBrowserField } from "./errorLocationAndBrowserField.js";
import { SupportFormWpPluginCompatibilityField } from "./errorWpPluginCompatibilityField.js";
import { SupportFormLicenseField } from "./licenseField.js";
import { SupportFormMessageField } from "./messageField.js";
import { SupportFormProductTypeField } from "./productTypeField.js";
import { SupportFormQuestionTypeField } from "./questionTypeField.js";
import { SupportFormScreenRecordingFormInput } from "./screenRecordingFormInput.js";
import { SupportFormTermField } from "./termField.js";
import { SupportFormUploadField } from "./uploadField.js";
import { SupportFormWpAccessDataField } from "./wpAccessDataField.js";
import { SupportFormWpHealthExportField } from "./wpHealthExportField.js";
import { IMPREZA_BUTTON_PROPS } from "../../components/provider.js";
import { RealCommerceError } from "../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../hooks/useRealCommerceErrorHandling.js";
import { useRecoverableSupportForm } from "../../hooks/useRecoverableSupportForm.js";
import { useSupportFormLanguage } from "../../hooks/useSupportFormLanguage.js";
import { __ } from "../../utils/i18n.js";
import { realCommerceRequest } from "../../utils/realCommerceRequest.js";
import { disableFomoCoupon } from "../fomoCoupon/banner.js";
const CLARITY_TAG_URL = "https://www.clarity.ms/tag/";
const getMicrosoftClarityProjectId = ()=>{
    var // The clarity SDK does currently not allow to read metadata, so do some hacky things...
    _window_performance_getEntriesByType_filter_;
    return (_window_performance_getEntriesByType_filter_ = window.performance.getEntriesByType("resource").filter((param)=>{
        let { name } = param;
        return name.startsWith(CLARITY_TAG_URL);
    })[0]) == null ? void 0 : _window_performance_getEntriesByType_filter_.name.split(CLARITY_TAG_URL)[1];
};
const SupportForm = (param)=>{
    let { questiontype: forceQuestionType, product: forceProduct, customercenter: customerCenter } = param;
    const ref = useRef();
    const [isLoading, setLoading] = useState(false);
    const [result, setResult] = useState(undefined);
    const languagesOfSupport = useSupportFormLanguage();
    const { form, initialValues, eraseMemorizedValues, makeRestorable } = useRecoverableSupportForm({
        forceProduct,
        forceQuestionType
    });
    const [emailMxDoNotExist, setEmailMxDoNotExist] = useState(false);
    const [wpInMaintenanceMode, setWpInMaintenanceMode] = useState(false);
    const [concernSubmitted, setConcernSubmitted] = useState(initialValues.concern || forceQuestionType === ESupportTicketQuestionType.RequestCustomOffer ? "not-suitable" : "initial");
    const onValuesChange = useCallback((changed, values)=>{
        if (Object.prototype.hasOwnProperty.call(changed, "email")) {
            setEmailMxDoNotExist(false);
        }
        if (Object.prototype.hasOwnProperty.call(changed, "errorLocation") || Object.prototype.hasOwnProperty.call(changed, "provideAccessData")) {
            setWpInMaintenanceMode(false);
        }
        if (Object.prototype.hasOwnProperty.call(changed, "questionType") && changed.questionType === ESupportTicketQuestionType.RequestCustomOffer) {
            setConcernSubmitted("not-suitable");
        }
        makeRestorable(values);
    }, [
        form
    ]);
    const handleRealCommerceError = useRealCommerceErrorHandling();
    /**
     * Finish handler when form gets submitted.
     */ const onFinish = useCallback(async (param)=>{
        let { name, email, product, bought, licenseKey, questionType, languageOfSupport, concern, companyName, numberOfNeededLicenses, screenRecording, wpSiteHealth, wpHealthExport, errorLocation, existsErrorInMultipleBrowsers, browserToReproduceError, errorPersistsWithoutProduct, errorPersistsWithoutThirdPartyProduct, faultyThirdPartyProduct, provideAccessData, accessDataUrl, accessDataUsername, accessDataPassword, message, attachments, disableEmailCheck, disableMaintenanceModeCheck } = param;
        const attachmentMap = {};
        if (attachments) {
            for(let i = 0; i < attachments.length; i++){
                attachmentMap[`attachment${i + 1}`] = attachments[i].originFileObj;
            }
        }
        setLoading(true);
        try {
            const microsoftClarityProjectId = getMicrosoftClarityProjectId();
            const { supportTicket } = await realCommerceRequest({
                location: routeLocationSupportTicketPost,
                multipart: true,
                request: {
                    supportTicket: {
                        name,
                        email,
                        product,
                        licenseKey: bought ? licenseKey : undefined,
                        questionType,
                        languageOfSupport,
                        subject: concern,
                        message: questionType === ESupportTicketQuestionType.RequestCustomOffer ? `${message}\n\n${__("Company name")}: ${companyName}\n${__("Number of needed licenses")}: ${numberOfNeededLicenses}` : message,
                        screenRecording,
                        ...attachmentMap,
                        technicalData: wpSiteHealth === "export" ? wpHealthExport : undefined,
                        errorLocation,
                        existsErrorInMultipleBrowsers: `${existsErrorInMultipleBrowsers}`,
                        browserToReproduceError: existsErrorInMultipleBrowsers ? undefined : browserToReproduceError,
                        errorPersistsWithoutProduct: `${errorPersistsWithoutProduct}`,
                        errorPersistsWithoutThirdPartyProduct: errorPersistsWithoutProduct ? undefined : errorPersistsWithoutThirdPartyProduct === "yes" ? "true" : errorPersistsWithoutThirdPartyProduct === "no" ? "false" : undefined,
                        faultyThirdPartyProduct,
                        accessDataUrl: provideAccessData ? accessDataUrl : undefined,
                        accessDataUsername: provideAccessData ? accessDataUsername : undefined,
                        accessDataPassword: provideAccessData ? accessDataPassword : undefined,
                        microsoftClarityProjectId,
                        userAgent: window.navigator.userAgent
                    },
                    disableEmailCheck,
                    disableMaintenanceModeCheck: provideAccessData ? true : disableMaintenanceModeCheck
                }
            });
            if (microsoftClarityProjectId) {
                window.clarity == null ? void 0 : window.clarity.call(window, "set", "support-ticket-id", `${supportTicket.id}`);
            }
            if (supportTicket.licenseReferencedCommerceProduct) {
                disableFomoCoupon({
                    reason: "user-of-product",
                    productId: supportTicket.licenseReferencedCommerceProduct.id
                });
            }
            setResult(supportTicket);
            eraseMemorizedValues();
            ref.current.scrollIntoView({
                behavior: "smooth"
            });
        } catch (e) {
            const error = new RealCommerceError(e);
            handleRealCommerceError(error);
            if (error.hasErrorCode("SupportTicketEmailMxDoNotExist")) {
                setEmailMxDoNotExist(error.message);
            }
            if (error.hasErrorCode("SupportTicketWpInMaintenanceMode")) {
                setWpInMaintenanceMode(error.message);
            }
            // This field needs to be checked by the user again
            form.setFieldsValue({
                disableEmailCheck: false,
                disableMaintenanceModeCheck: false
            });
        } finally{
            setLoading(false);
        }
    }, []);
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        children: /*#__PURE__*/ _jsx(Spin, {
            spinning: isLoading,
            children: result ? /*#__PURE__*/ _jsx(Result, {
                status: "success",
                title: result.questionType === ESupportTicketQuestionType.RequestCustomOffer ? __("Request created") : __("Support ticket created"),
                subTitle: __("Thanks for your message. We will response as soon as possible!")
            }) : /*#__PURE__*/ _jsxs(Form, {
                form: form,
                onFinish: onFinish,
                colon: false,
                initialValues: initialValues,
                onValuesChange: onValuesChange,
                layout: "vertical",
                labelWrap: true,
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Name"),
                        name: "name",
                        required: true,
                        rules: [
                            {
                                required: true,
                                message: __("Please enter your name!")
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {})
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Email"),
                        name: "email",
                        required: true,
                        rules: [
                            {
                                required: true,
                                type: "email",
                                message: __("Please enter a valid email address!")
                            }
                        ],
                        ...emailMxDoNotExist ? {
                            hasFeedback: true,
                            validateStatus: "error",
                            help: emailMxDoNotExist
                        } : {},
                        children: /*#__PURE__*/ _jsx(Input, {})
                    }),
                    emailMxDoNotExist && /*#__PURE__*/ _jsx(Form.Item, {
                        name: "disableEmailCheck",
                        valuePropName: "checked",
                        label: __("Confirm email address"),
                        rules: [
                            {
                                type: "boolean",
                                required: true,
                                transform: (value)=>value || undefined,
                                message: __("Please confirm that you have entered the correct e-mail address!")
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Checkbox, {
                            children: __("This email address is correct")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        label: __("Language of your request"),
                        required: true,
                        name: "languageOfSupport",
                        rules: [
                            {
                                required: true,
                                message: __("Please choose one option!")
                            }
                        ],
                        style: {
                            display: forceProduct ? "none" : undefined
                        },
                        children: /*#__PURE__*/ _jsx(Select, {
                            children: Object.keys(languagesOfSupport).map((key)=>/*#__PURE__*/ _jsx(Select.Option, {
                                    value: languagesOfSupport[key]["value"],
                                    children: languagesOfSupport[key]["localized"]
                                }, key))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SupportFormProductTypeField, {
                        forceProduct: forceProduct
                    }),
                    /*#__PURE__*/ _jsx(SupportFormLicenseField, {
                        customerCenter: customerCenter
                    }),
                    /*#__PURE__*/ _jsx(SupportFormQuestionTypeField, {
                        forceProduct: forceProduct
                    }),
                    /*#__PURE__*/ _jsx(SupportFormConcernField, {
                        concernSubmitted: concernSubmitted,
                        onArticlesLoaded: ()=>setConcernSubmitted("loaded"),
                        onArticlesNotSuitable: ()=>setConcernSubmitted("not-suitable")
                    }),
                    concernSubmitted === "not-suitable" && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Divider, {
                                orientation: "left",
                                children: __("More details about your request")
                            }),
                            /*#__PURE__*/ _jsx(SupportFormWpPluginCompatibilityField, {}),
                            /*#__PURE__*/ _jsx(SupportFormErrorLocationAndBrowserField, {
                                wpInMaintenanceMode: wpInMaintenanceMode
                            }),
                            /*#__PURE__*/ _jsx(SupportFormWpAccessDataField, {}),
                            /*#__PURE__*/ _jsx(SupportFormWpHealthExportField, {}),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                noStyle: true,
                                shouldUpdate: (prevValues, curValues)=>prevValues.questionType !== curValues.questionType,
                                children: (param)=>{
                                    let { getFieldValue } = param;
                                    return !!getFieldValue("questionType") && [
                                        ESupportTicketQuestionType.ProblemWithProduct,
                                        ESupportTicketQuestionType.ProblemWithLicenseOrAccount
                                    ].indexOf(getFieldValue("questionType")) > -1 && /*#__PURE__*/ _jsx(Form.Item, {
                                        label: __("Would you like to provide us a screen recording showing the issue?"),
                                        name: "screenRecording",
                                        extra: __("You can record your screen without installing any additional software. Your browser will ask you to allow screen recording and the use of your microphone (optional)."),
                                        children: /*#__PURE__*/ _jsx(SupportFormScreenRecordingFormInput, {})
                                    });
                                }
                            }),
                            /*#__PURE__*/ _jsx(SupportFormMessageField, {}),
                            /*#__PURE__*/ _jsx(SupportFormUploadField, {}),
                            /*#__PURE__*/ _jsx(SupportFormTermField, {})
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        style: {
                            textAlign: "center"
                        },
                        wrapperCol: {
                            span: 24
                        },
                        shouldUpdate: (prevValues, nextValues)=>prevValues.questionType !== nextValues.questionType || prevValues.product !== nextValues.product || prevValues.screenRecording !== nextValues.screenRecording || prevValues.attachments !== nextValues.attachments,
                        children: (param)=>{
                            let { getFieldValue } = param;
                            const questionType = getFieldValue("questionType");
                            const isCustomOfferRequest = questionType === ESupportTicketQuestionType.RequestCustomOffer;
                            if (questionType && !isCustomOfferRequest && concernSubmitted !== "not-suitable") {
                                return null;
                            }
                            const hasInsufficientData = [
                                ESupportTicketQuestionType.ProblemWithLicenseOrAccount,
                                ESupportTicketQuestionType.ProblemWithProduct
                            ].indexOf(questionType) > -1 && !getFieldValue("screenRecording") && !getFieldValue("attachments").length;
                            const btn = /*#__PURE__*/ _jsx(Button, {
                                type: "primary",
                                size: "large",
                                ...IMPREZA_BUTTON_PROPS,
                                htmlType: hasInsufficientData ? "button" : "submit",
                                children: questionType === ESupportTicketQuestionType.RequestCustomOffer ? __("Request a quote") : __("Open support ticket")
                            });
                            return hasInsufficientData ? /*#__PURE__*/ _jsx(Popconfirm, {
                                title: __("Is your problem really described sufficiently without a screenshot or screen recording? The better we understand your problem, the faster we can help you!"),
                                cancelText: __("Cancel"),
                                okText: __("Send anyway"),
                                overlayStyle: {
                                    maxWidth: 450
                                },
                                onConfirm: form.submit,
                                placement: "top",
                                children: btn
                            }) : btn;
                        }
                    })
                ]
            })
        })
    });
};
export { SupportForm };
